import { useCallback, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Modal } from '../../components/Modal';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import { formatInstantDate, formatRelativeAge } from '../../lib/format';
import { countCharacters, PLATFORM_OPTIONS, platformLabel } from '../../lib/platforms';
import type {
  BrandMemoryCreate,
  BrandMemoryEvidence,
  BrandMemoryKind,
  BrandMemoryRead,
  BrandMemoryScope,
  BrandMemoryUpdate,
  BrandSuggestionRead,
} from '../../types/api';
import {
  BRAND_MEMORY_KIND_OPTIONS,
  BRAND_MEMORY_SCOPE_OPTIONS,
  evidenceActionLabel,
  evidenceHref,
  evidenceKey,
  groupBrandMemories,
  kindLabel,
  pendingSignalsLabel,
  scopeLabel,
  sourceBadge,
  suggestionAcceptedNotice,
  suggestionHeadline,
} from './brandMemory';
import { BRAND_MEMORY_LIMIT, BRAND_MEMORY_MAX_LENGTH, brandMemoriesApi } from './brandMemoryApi';
import { BrandMemorySwitch } from './BrandMemorySwitch';
import { SectionShell } from './SectionShell';
import { useBrandMemoryState } from './useBrandMemoryState';
import { useBrandSuggestions } from './useBrandSuggestions';
import { useCollection } from './useCollection';

function messageFor(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) {
    switch (caught.code) {
      case 'BRAND_MEMORY_LIMIT_REACHED':
        return `Brand memory is full (${BRAND_MEMORY_LIMIT}). Forget or delete one first.`;
      case 'RESOURCE_NOT_FOUND':
        return 'That memory no longer exists.';
    }
  }
  return getErrorMessage(caught, fallback);
}

/** The editable fields of a memory, as the form holds them. Platform "" = any. */
interface MemoryFields {
  content: string;
  scope: BrandMemoryScope;
  kind: BrandMemoryKind;
  platform: string;
}

const EMPTY_FIELDS: MemoryFields = { content: '', scope: 'all', kind: 'prefer', platform: '' };

function fieldsOf(memory: BrandMemoryRead): MemoryFields {
  return {
    content: memory.content,
    scope: memory.scope,
    kind: memory.kind,
    platform: memory.platform ?? '',
  };
}

/** Only what changed, so a learned memory is not made `stated` by a scope tweak. */
function changesFrom(memory: BrandMemoryRead, fields: MemoryFields): BrandMemoryUpdate {
  const body: BrandMemoryUpdate = {};
  const content = fields.content.trim();
  if (content !== memory.content) body.content = content;
  if (fields.scope !== memory.scope) body.scope = fields.scope;
  if (fields.kind !== memory.kind) body.kind = fields.kind;
  const platform = fields.platform || null;
  if (platform !== memory.platform) body.platform = platform;
  return body;
}

interface MemoryFieldsEditorProps {
  idPrefix: string;
  fields: MemoryFields;
  onChange: (next: MemoryFields) => void;
}

/** Content, then scope, kind and platform. Shared by the add form and the inline edit. */
function MemoryFieldsEditor({ idPrefix, fields, onChange }: MemoryFieldsEditorProps) {
  const count = countCharacters(fields.content);
  const isOver = count > BRAND_MEMORY_MAX_LENGTH;

  return (
    <div className="brand-memory-fields">
      <label className="field">
        <span className="field__label">Memory</span>
        <textarea
          className="field__input memory-row__input"
          id={`${idPrefix}-content`}
          rows={2}
          value={fields.content}
          placeholder="Keep captions under three short paragraphs"
          onChange={(event) => onChange({ ...fields, content: event.target.value })}
        />
        <span
          className={isOver ? 'memory-row__count memory-row__count--over' : 'memory-row__count'}
        >
          {count}/{BRAND_MEMORY_MAX_LENGTH}
        </span>
      </label>

      <div className="brand-memory-fields__row">
        <label className="field">
          <span className="field__label">Applies to</span>
          <select
            className="field__input"
            value={fields.scope}
            onChange={(event) =>
              onChange({ ...fields, scope: event.target.value as BrandMemoryScope })
            }
          >
            {BRAND_MEMORY_SCOPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field__label">Type</span>
          <select
            className="field__input"
            value={fields.kind}
            onChange={(event) =>
              onChange({ ...fields, kind: event.target.value as BrandMemoryKind })
            }
          >
            {BRAND_MEMORY_KIND_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field__label">Platform</span>
          <select
            className="field__input"
            value={fields.platform}
            onChange={(event) => onChange({ ...fields, platform: event.target.value })}
          >
            <option value="">Any platform</option>
            {PLATFORM_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
            {/* A slug this build does not list still shows as itself. */}
            {fields.platform && !PLATFORM_OPTIONS.some((o) => o.value === fields.platform) ? (
              <option value={fields.platform}>{platformLabel(fields.platform)}</option>
            ) : null}
          </select>
        </label>
      </div>
    </div>
  );
}

function canSave(fields: MemoryFields): boolean {
  const length = countCharacters(fields.content.trim());
  return length > 0 && length <= BRAND_MEMORY_MAX_LENGTH;
}

/**
 * "Why (3)": the signals a memory or suggestion came from, each linking to
 * where it happened (the review screen, or the assistant for a plan).
 */
function EvidenceDisclosure({ evidence }: { evidence: readonly BrandMemoryEvidence[] }) {
  if (evidence.length === 0) return null;
  return (
    <details className="brand-memory-row__evidence">
      <summary className="brand-memory-row__evidence-summary">Why ({evidence.length})</summary>
      <ul className="brand-memory-row__evidence-list">
        {evidence.map((entry, index) => {
          const href = evidenceHref(entry);
          const label = evidenceActionLabel(entry.action);
          return (
            <li key={evidenceKey(entry, index)}>
              {href ? <Link to={href}>{label}</Link> : label}{' '}
              <span className="memory-row__stamp">{formatInstantDate(entry.at)}</span>
            </li>
          );
        })}
      </ul>
    </details>
  );
}

interface MemoryItemProps {
  memory: BrandMemoryRead;
  onUpdate: (body: BrandMemoryUpdate) => Promise<unknown>;
  onDelete: () => void;
}

/** One memory: what it says, where it applies, why it is here, and what can be done to it. */
function MemoryItem({ memory, onUpdate, onDelete }: MemoryItemProps) {
  const [editing, setEditing] = useState<MemoryFields | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(body: BrandMemoryUpdate, fallback: string): Promise<boolean> {
    setIsBusy(true);
    setError(null);
    try {
      await onUpdate(body);
      return true;
    } catch (caught) {
      setError(messageFor(caught, fallback));
      return false;
    } finally {
      setIsBusy(false);
    }
  }

  async function save() {
    if (editing === null || !canSave(editing)) return;
    const body = changesFrom(memory, editing);
    if (Object.keys(body).length === 0) {
      setEditing(null);
      return;
    }
    // Only on success: an edit that did not save stays open.
    if (await run(body, 'That memory could not be saved.')) setEditing(null);
  }

  const isCandidate = memory.status === 'candidate';
  const isActive = memory.status === 'active';
  const isRetired = memory.status === 'retired';

  return (
    <li className={memory.pinned ? 'memory-row brand-memory-row--pinned' : 'memory-row'}>
      {editing === null ? (
        <>
          <p className="memory-row__text">{memory.content}</p>
          <div className="brand-memory-row__chips">
            <span className="badge badge--neutral">{sourceBadge(memory)}</span>
            {isRetired && memory.retired_by === 'system' ? (
              <span className="badge badge--discontinued">Dropped by Autopostmate</span>
            ) : null}
            <span className="intent-tag">{scopeLabel(memory.scope)}</span>
            {memory.platform ? (
              <span className="intent-tag">{platformLabel(memory.platform)}</span>
            ) : null}
            <span className="intent-tag">{kindLabel(memory.kind)}</span>
          </div>

          <EvidenceDisclosure evidence={memory.evidence} />

          <div className="memory-row__actions brand-memory-row__actions">
            {isCandidate ? (
              <>
                <button
                  className="button button--primary memory-row__action"
                  type="button"
                  disabled={isBusy}
                  onClick={() =>
                    void run({ status: 'active' }, 'That memory could not be confirmed.')
                  }
                >
                  Confirm
                </button>
                <button
                  className="button button--ghost memory-row__action"
                  type="button"
                  disabled={isBusy}
                  onClick={() =>
                    void run({ status: 'retired' }, 'That memory could not be dismissed.')
                  }
                >
                  Dismiss
                </button>
              </>
            ) : null}
            {isActive ? (
              <button
                className="button button--ghost memory-row__action"
                type="button"
                aria-pressed={memory.pinned}
                title="A pinned memory is always used, even when the list is long."
                disabled={isBusy}
                onClick={() =>
                  void run({ pinned: !memory.pinned }, 'That memory could not be pinned.')
                }
              >
                {memory.pinned ? 'Unpin' : 'Pin'}
              </button>
            ) : null}
            {isRetired ? (
              <button
                className="button button--ghost memory-row__action"
                type="button"
                disabled={isBusy}
                onClick={() => void run({ status: 'active' }, 'That memory could not be restored.')}
              >
                Restore
              </button>
            ) : (
              <button
                className="button button--ghost memory-row__action"
                type="button"
                disabled={isBusy}
                onClick={() => {
                  setError(null);
                  setEditing(fieldsOf(memory));
                }}
              >
                Edit
              </button>
            )}
            {isActive ? (
              <button
                className="button button--ghost memory-row__action"
                type="button"
                title="Stop using it. Autopostmate will not learn it again."
                disabled={isBusy}
                onClick={() =>
                  void run({ status: 'retired' }, 'That memory could not be forgotten.')
                }
              >
                Forget
              </button>
            ) : null}
            <button
              className="button button--danger-text memory-row__action"
              type="button"
              disabled={isBusy}
              onClick={onDelete}
            >
              Delete
            </button>
          </div>
        </>
      ) : (
        <>
          <MemoryFieldsEditor
            idPrefix={`brand-memory-${memory.id}`}
            fields={editing}
            onChange={setEditing}
          />
          {memory.source === 'learned' && editing.content.trim() !== memory.content ? (
            <p className="brand-memory__quiet">
              Changing the words marks it as something you said.
            </p>
          ) : null}
          <div className="memory-row__actions brand-memory-row__actions">
            <button
              className="button button--ghost memory-row__action"
              type="button"
              disabled={isBusy}
              onClick={() => {
                setEditing(null);
                setError(null);
              }}
            >
              Cancel
            </button>
            <button
              className="button memory-row__action"
              type="button"
              disabled={isBusy || !canSave(editing)}
              onClick={() => void save()}
            >
              {isBusy ? 'Saving' : 'Save'}
            </button>
          </div>
        </>
      )}

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}
    </li>
  );
}

interface AddMemoryFormProps {
  onSubmit: (body: BrandMemoryCreate) => Promise<unknown>;
  onDone: () => void;
}

function AddMemoryForm({ onSubmit, onDone }: AddMemoryFormProps) {
  const [fields, setFields] = useState<MemoryFields>(EMPTY_FIELDS);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSave(fields) || isSaving) return;
    setIsSaving(true);
    setError(null);
    try {
      await onSubmit({
        content: fields.content.trim(),
        scope: fields.scope,
        kind: fields.kind,
        platform: fields.platform || null,
      });
      onDone();
    } catch (caught) {
      setError(messageFor(caught, 'That memory could not be saved.'));
      setIsSaving(false);
    }
  }

  return (
    <form className="form" onSubmit={(event) => void handleSubmit(event)} noValidate>
      <MemoryFieldsEditor idPrefix="brand-memory-new" fields={fields} onChange={setFields} />

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="modal__actions">
        <button className="button" type="button" onClick={onDone} disabled={isSaving}>
          Cancel
        </button>
        <button
          className="button button--primary"
          type="submit"
          disabled={isSaving || !canSave(fields)}
        >
          {isSaving ? 'Saving' : 'Save memory'}
        </button>
      </div>
    </form>
  );
}

interface SuggestionCardProps {
  suggestion: BrandSuggestionRead;
  /** Resolves when it is decided; throws with the message to show on the card. */
  onAccept: () => Promise<void>;
  onDismiss: () => Promise<void>;
}

/** One proposed change to the brand identity, with why and the two ways to answer it. */
function SuggestionCard({ suggestion, onAccept, onDismiss }: SuggestionCardProps) {
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(action: () => Promise<void>, fallback: string) {
    setIsBusy(true);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(getErrorMessage(caught, fallback));
      setIsBusy(false);
    }
  }

  return (
    <li className="memory-row">
      <p className="memory-row__text">{suggestionHeadline(suggestion)}</p>
      {suggestion.reason ? <p className="brand-memory__quiet">{suggestion.reason}</p> : null}
      <EvidenceDisclosure evidence={suggestion.evidence} />
      <div className="memory-row__actions brand-memory-row__actions">
        <button
          className="button button--primary memory-row__action"
          type="button"
          disabled={isBusy}
          onClick={() => void run(onAccept, 'That change could not be made.')}
        >
          Accept
        </button>
        <button
          className="button button--ghost memory-row__action"
          type="button"
          title="Autopostmate will not suggest this again."
          disabled={isBusy}
          onClick={() => void run(onDismiss, 'That suggestion could not be dismissed.')}
        >
          Dismiss
        </button>
      </div>
      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}
    </li>
  );
}

interface MemoryGroupProps {
  title: string;
  lede?: string;
  memories: BrandMemoryRead[];
  onUpdate: (memory: BrandMemoryRead, body: BrandMemoryUpdate) => Promise<unknown>;
  onDelete: (memory: BrandMemoryRead) => void;
}

function MemoryList({ memories, onUpdate, onDelete }: Omit<MemoryGroupProps, 'title' | 'lede'>) {
  return (
    <ul className="memory-list brand-memory__list">
      {memories.map((memory) => (
        <MemoryItem
          key={memory.id}
          memory={memory}
          onUpdate={(body) => onUpdate(memory, body)}
          onDelete={() => onDelete(memory)}
        />
      ))}
    </ul>
  );
}

function MemoryGroup({ title, lede, memories, onUpdate, onDelete }: MemoryGroupProps) {
  if (memories.length === 0) return null;
  return (
    <section className="brand-memory__group">
      <h3 className="brand-memory__group-title">
        {title} <span className="brand-memory__group-count">{memories.length}</span>
      </h3>
      {lede ? <p className="brand-memory__group-lede">{lede}</p> : null}
      <MemoryList memories={memories} onUpdate={onUpdate} onDelete={onDelete} />
    </section>
  );
}

/**
 * Brand memory: the lasting preferences every agent reads. Some the user
 * stated (here, or to the assistant); the rest Autopostmate learned from how posts
 * were reviewed. Learned ones seen once arrive as suggestions to confirm.
 *
 * Forget and Delete are different on purpose: a forgotten memory is kept so it
 * is never learned again, a deleted one is gone and may come back.
 */
export function BrandMemorySection({ businessId }: { businessId: string }) {
  const memories = useCollection(brandMemoriesApi, businessId, 'brand_memories');
  const suggestions = useBrandSuggestions(businessId);
  const refreshMemories = memories.refresh;
  const refreshSuggestions = suggestions.refresh;
  const onLearned = useCallback(() => {
    refreshMemories();
    refreshSuggestions();
  }, [refreshMemories, refreshSuggestions]);
  const learning = useBrandMemoryState(businessId, onLearned);
  /** After an accept, or a suggestion that turned out to be decided already. */
  const [suggestionNotice, setSuggestionNotice] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [deleting, setDeleting] = useState<BrandMemoryRead | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const groups = groupBrandMemories(memories.items);
  const state = learning.state;
  const pendingSignals = state?.pending_signals ?? 0;

  async function update(memory: BrandMemoryRead, body: BrandMemoryUpdate) {
    try {
      return await memories.update(memory.id, body);
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'RESOURCE_NOT_FOUND') memories.refresh();
      throw caught;
    }
  }

  async function confirmDelete() {
    if (deleting === null) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await memories.remove(deleting.id);
      setDeleting(null);
    } catch (caught) {
      setDeleteError(messageFor(caught, 'That memory could not be deleted.'));
    } finally {
      setIsDeleting(false);
    }
  }

  /**
   * A suggestion someone already decided (another tab, another member), or one
   * that is gone, leaves the list with the reason said above it. Anything else
   * stays on the card, so a conflict can be read beside what it is about.
   */
  async function decide(
    suggestion: BrandSuggestionRead,
    action: 'accept' | 'dismiss',
  ): Promise<void> {
    setSuggestionNotice(null);
    try {
      if (action === 'accept') {
        await suggestions.accept(suggestion);
        setSuggestionNotice(suggestionAcceptedNotice(suggestion));
      } else {
        await suggestions.dismiss(suggestion);
      }
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'SUGGESTION_NOT_PENDING') {
        setSuggestionNotice(getErrorMessage(caught, 'That suggestion was already decided.'));
        suggestions.refresh();
        return;
      }
      if (caught instanceof ApiError && caught.code === 'RESOURCE_NOT_FOUND') {
        setSuggestionNotice('That suggestion no longer exists.');
        suggestions.refresh();
        return;
      }
      throw caught;
    }
  }

  function startAdd() {
    setIsAdding(true);
  }

  const learnStatus = learning.isLoading
    ? 'Checking'
    : state === null
      ? null
      : `${state.last_run_at ? `Last learned ${formatRelativeAge(state.last_run_at)}` : 'Not learned yet'} · ${pendingSignalsLabel(pendingSignals)}`;

  return (
    <>
      <SectionShell
        title="What Autopostmate has learned"
        lede="Preferences every new post follows. Some you told us; the rest we picked up from how you review posts."
        action={
          <div className="brand-memory__learn">
            {learnStatus ? <p className="brand-memory__learn-status">{learnStatus}</p> : null}
            <button
              className="button"
              type="button"
              disabled={learning.isLearning || learning.isLoading || pendingSignals === 0}
              onClick={() => void learning.learn()}
            >
              {learning.isLearning ? 'Learning…' : 'Learn now'}
            </button>
            <button className="button button--primary" type="button" onClick={startAdd}>
              Add memory
            </button>
          </div>
        }
        aside={
          <>
            <BrandMemorySwitch businessId={businessId} />
            {state?.last_error ? (
              <p className="brand-memory__quiet">Last update did not finish: {state.last_error}</p>
            ) : null}
            {learning.loadError ? (
              <p className="brand-memory__quiet">{learning.loadError}</p>
            ) : null}
            {learning.learnNotice ? (
              <p className="brand-memory__quiet" role="status">
                {learning.learnNotice}
              </p>
            ) : null}
            {learning.learnError ? (
              <p className="form__error" role="alert">
                {learning.learnError}
              </p>
            ) : null}
            {suggestions.error ? <p className="brand-memory__quiet">{suggestions.error}</p> : null}
            {suggestionNotice ? (
              <p className="brand-memory__quiet" role="status">
                {suggestionNotice}
              </p>
            ) : null}
          </>
        }
        isLoading={memories.isLoading || suggestions.isLoading}
        error={memories.error}
        isEmpty={memories.items.length === 0 && suggestions.items.length === 0}
        emptyMessage="Nothing yet. Autopostmate learns as you edit, pick and approve posts, or add a preference yourself."
        emptyAction={
          <button className="button" type="button" onClick={startAdd}>
            Add memory
          </button>
        }
      >
        <div className="brand-memory">
          {suggestions.items.length > 0 ? (
            <section className="brand-memory__group">
              <h3 className="brand-memory__group-title">
                Suggested changes{' '}
                <span className="brand-memory__group-count">{suggestions.items.length}</span>
              </h3>
              <p className="brand-memory__group-lede">
                Rules for your brand identity, learned from your reviews. Nothing changes until you
                accept.
              </p>
              <ul className="memory-list brand-memory__list">
                {suggestions.items.map((suggestion) => (
                  <SuggestionCard
                    key={suggestion.id}
                    suggestion={suggestion}
                    onAccept={() => decide(suggestion, 'accept')}
                    onDismiss={() => decide(suggestion, 'dismiss')}
                  />
                ))}
              </ul>
            </section>
          ) : null}
          <MemoryGroup
            title="Suggestions"
            lede="Seen in one post so far. Confirm to use it, or dismiss it."
            memories={groups.suggestions}
            onUpdate={update}
            onDelete={setDeleting}
          />
          <MemoryGroup
            title="Active"
            memories={groups.active}
            onUpdate={update}
            onDelete={setDeleting}
          />
          {groups.forgotten.length > 0 ? (
            <details className="brand-memory__group brand-memory__forgotten">
              <summary className="brand-memory__group-title brand-memory__forgotten-summary">
                Forgotten{' '}
                <span className="brand-memory__group-count">{groups.forgotten.length}</span>
              </summary>
              <p className="brand-memory__group-lede">Not used. Restore one to use it again.</p>
              <MemoryList memories={groups.forgotten} onUpdate={update} onDelete={setDeleting} />
            </details>
          ) : null}
        </div>
      </SectionShell>

      {isAdding ? (
        <Modal title="Add a memory" kind="memory" onDismiss={() => setIsAdding(false)}>
          <AddMemoryForm onSubmit={memories.create} onDone={() => setIsAdding(false)} />
        </Modal>
      ) : null}

      {deleting ? (
        <ConfirmDialog
          title="Delete this memory?"
          message="Deleting removes it completely, so Autopostmate may learn it again later. To stop that, use Forget instead."
          confirmLabel="Delete memory"
          busyLabel="Deleting"
          isBusy={isDeleting}
          error={deleteError}
          onConfirm={() => void confirmDelete()}
          onDismiss={() => {
            setDeleting(null);
            setDeleteError(null);
          }}
        />
      ) : null}
    </>
  );
}
